import { Box, Stack, TextField, Typography } from '@mui/material'
import React, { useContext } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { CodeBlock } from 'components/common/CodeBlock'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import { TutorialStep } from 'components/common/TutorialStep'
import Icon from 'components/Icon'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'

import { ManagedPolicies } from './steps/ManagedPolicies'
import { PermissionPolicy } from './steps/PermissionPolicy'

export const AWSManualRoleAuthentication: React.FC = () => {
  const { trans } = useTranslation()
  const { control, formState } = useFormContext<StatusCheckFormValues>()

  const { awsIAMRoleMetadata, isEdit } = useContext(
    ObservabilityToolAuthenticationContext
  )

  return (
    <Stack spacing={2}>
      <Typography>
        {trans('status_checks.authentication.aws.iam_role.manual_method.title')}
      </Typography>
      <TutorialStep
        badgeNumber={1}
        title={trans(
          'status_checks.authentication.aws.iam_role.manual_method.step_1.title'
        )}
      >
        <Box ml={1}>
          <Typography>
            {trans(
              'status_checks.authentication.aws.iam_role.manual_method.step_1.description.part_1'
            )}{' '}
            <TrackedExternalLink href="https://www.gremlin.com/docs/platform-health-checks-amazon-cloudwatch">
              {trans(
                'status_checks.authentication.aws.iam_role.manual_method.step_1.description.link.label'
              )}{' '}
              <Icon
                css={{ height: theme.fontSizes[0], verticalAlign: 'baseline' }}
                icon="arrowUpRightFromSquare"
              />
            </TrackedExternalLink>{' '}
            {trans(
              'status_checks.authentication.aws.iam_role.manual_method.step_1.description.part_2'
            )}
          </Typography>
        </Box>
      </TutorialStep>
      <PermissionPolicy badeCount={2} />
      <TutorialStep
        badgeNumber={3}
        title={trans(
          'status_checks.authentication.aws.iam_role.manual_method.step_3.title'
        )}
      >
        <Box ml={1} mt={1}>
          <CodeBlock
            code={awsIAMRoleMetadata?.trustPolicy}
            includeLineNumbers={false}
            isCopyEnabled={true}
          />
        </Box>
      </TutorialStep>
      <ManagedPolicies badgeCount={4} />
      <TutorialStep
        badgeNumber={5}
        title={trans(
          'status_checks.authentication.aws.iam_role.last_step.title'
        )}
      >
        <Box ml={1} mt={1}>
          <Stack spacing={2}>
            <Typography>
              {trans(
                'status_checks.authentication.aws.iam_role.last_step.description'
              )}
            </Typography>
            <Controller
              control={control}
              name="awsIamRoleArn"
              render={({ field }) => (
                <TextField
                  {...field}
                  autoComplete="off"
                  data-testid="aws-iam-role-arn-input"
                  error={Boolean(formState.errors.awsIamRoleArn)}
                  helperText={
                    isEdit && !formState.errors?.awsIamRoleArn
                      ? trans(
                          'status_checks.authentication.aws.iam_role.arn_input.edit.message'
                        )
                      : formState.errors?.awsIamRoleArn?.message
                  }
                  label={trans(
                    'status_checks.authentication.aws_iam_role_arn.text_field.label'
                  )}
                  variant="outlined"
                />
              )}
            />
          </Stack>
        </Box>
      </TutorialStep>
    </Stack>
  )
}
